/**
 * Lockstep Netcode Engine
 *
 * Both players run the emulator locally. Neither advances until both have
 * exchanged input for the current frame. No prediction, no rollback, no
 * state saving — identical inputs in identical order on deterministic
 * cores means zero desync by construction.
 *
 * Frame advancement:
 *   1. Capture local input, buffer it D frames ahead, send to peer
 *   2. Wait until remote input for currentFrame is available
 *   3. Apply both inputs, step the emulator
 *   4. Repeat
 *
 * D (input delay) is adaptive: ceil(RTT/2 / frameDuration) + 1 jitter frame.
 * Both players experience the same input delay — no asymmetry.
 */

const FRAME_DURATION_MS_DEFAULT = 1000 / 60; // fallback only; main.js passes per-system fps
const JITTER_BUFFER_MS = 16;                 // one NTSC frame of headroom
const MIN_DELAY = 1;
// MAX_DELAY = 8 caps the buffer at ~133ms at 60fps. Above ~180ms RTT the formula
// wants more than 8 frames and the engine silently falls back to stalls. Revisit
// if Asia/EU pairs become common.
const MAX_DELAY = 8;
const SYNC_INTERVAL = 120;
const RTT_HISTORY_SIZE = 60;                 // 30 seconds at 500ms ping cadence
const RTT_PERCENTILE = 0.90;
const DELAY_DECREASE_SAMPLES = 16;           // ~8 seconds of sustained-low P90 before dropping
const MIN_HISTORY_FOR_P90 = 4;               // single-sample bootstrap until we have this many

export function computeLockstepInputDelay(
  rttMs,
  frameDurationMs = FRAME_DURATION_MS_DEFAULT,
  jitterMs = JITTER_BUFFER_MS,
) {
  const rtt = Number.isFinite(rttMs) && rttMs >= 0 ? rttMs : 0;
  const frameMs = Number.isFinite(frameDurationMs) && frameDurationMs > 0
    ? frameDurationMs
    : FRAME_DURATION_MS_DEFAULT;
  const target = Math.ceil((rtt / 2 + jitterMs) / frameMs);
  return Math.max(MIN_DELAY, Math.min(MAX_DELAY, target));
}

export class LockstepEngine {
  /**
   * @param {import('./cores/core.js').EmulatorCore} emulator
   * @param {number} localPlayer  0 or 1
   * @param {object} opts
   * @param {number} [opts.inputDelay=2]
   * @param {number} [opts.startFrame=0]
   * @param {number} [opts.frameDurationMs] system-specific frame duration; defaults to 60fps
   */
  constructor(emulator, localPlayer, opts = {}) {
    this.emu = emulator;
    this.localPlayer = localPlayer;
    this.remotePlayer = 1 - localPlayer;

    this.currentFrame = opts.startFrame ?? 0;
    this.inputDelay = opts.inputDelay ?? 2;
    this._frameDurationMs = opts.frameDurationMs ?? FRAME_DURATION_MS_DEFAULT;

    this.localInputs = {};
    this.remoteInputs = {};
    this._pendingLocalInput = 0;

    // Pre-seed both sides for the initial delay window so advance()
    // doesn't deadlock waiting for remote input that was never sent.
    for (let f = this.currentFrame; f < this.currentFrame + this.inputDelay; f++) {
      this.localInputs[f] = 0;
      this.remoteInputs[f] = 0;
    }
    this._nextLocalInputFrame = this.currentFrame + this.inputDelay;

    // Stats
    this.stallCount = 0;
    this.totalFrames = 0;
    this._stallStart = 0;
    this._totalStallMs = 0;

    // Desync detection
    this._syncInterval = SYNC_INTERVAL;
    this._lastSyncFrame = this.currentFrame;
    this._localSyncHashes = {};
    this._remoteSyncHashes = {};
    this.desyncDetected = false;
    this.desyncFrame = -1;
    this.onDesync = null;
    this.onSendSync = null;

    // Adaptive delay (P90 over rolling RTT history, single-sample bootstrap until 4 samples)
    this._rttSamples = [];
    this._decreaseStreak = 0;
    this._lastRtt = 0;

    // Network callbacks
    this.onSendInput = null;

    // Observer hook: fired once per advanced frame, *after* inputs are applied
    // to the emulator and *before* stepFrame(). Receives (frame, p1, p2) in
    // canonical player order (p1 is always player-0 input, regardless of which
    // side is local). Used by the replay recorder.
    this.onApplyInputs = null;
  }

  setLocalInput(bitmask) {
    this._pendingLocalInput = bitmask;
  }

  receiveRemoteInput(frame, bitmask) {
    this.remoteInputs[frame] = bitmask;
  }

  receiveRemoteSyncHash(frame, hash) {
    this._remoteSyncHashes[frame] = hash;
    this._checkDesync(frame);
  }

  /**
   * Attempt to advance one frame.
   * Returns true if the frame was advanced, false if stalled (waiting for remote input).
   */
  advance() {
    this._sendLocalInputsThrough(this.currentFrame + this.inputDelay);

    // Check if we have remote input for the current frame
    if (this.remoteInputs[this.currentFrame] === undefined) {
      if (!this._stallStart) {
        this._stallStart = performance.now();
        this.stallCount++;
      }
      return false;
    }

    // End stall tracking
    if (this._stallStart) {
      this._totalStallMs += performance.now() - this._stallStart;
      this._stallStart = 0;
    }

    // Apply both inputs
    const localInput = this.localInputs[this.currentFrame] ?? 0;
    const remoteInput = this.remoteInputs[this.currentFrame];

    let p1Input, p2Input;
    if (this.localPlayer === 0) {
      p1Input = localInput;
      p2Input = remoteInput;
    } else {
      p1Input = remoteInput;
      p2Input = localInput;
    }
    this.emu.setButtons(0, p1Input);
    this.emu.setButtons(1, p2Input);

    // Notify observers (replay recorder, etc.) with canonical player order.
    if (this.onApplyInputs) {
      this.onApplyInputs(this.currentFrame, p1Input, p2Input);
    }

    // Step the emulator
    this.emu.stepFrame();
    this.currentFrame++;
    this.totalFrames++;

    // Periodic desync detection
    if (this.currentFrame - this._lastSyncFrame >= this._syncInterval) {
      this._lastSyncFrame = this.currentFrame;
      const hash = this.emu.stateFingerprint();
      this._localSyncHashes[this.currentFrame] = hash;
      if (this.onSendSync) {
        this.onSendSync(this.currentFrame, hash);
      }
      this._checkDesync(this.currentFrame);
    }

    this._gc();
    return true;
  }

  /**
   * Record one RTT sample and (possibly) adjust input delay.
   *
   * The basis for the delay calculation is the P90 of recent samples once we
   * have enough history (>= MIN_HISTORY_FOR_P90). Until then, the latest single
   * sample is used so the first ~2 seconds of a session don't stall at the
   * default 2-frame delay on a high-latency link (cold-start bootstrap).
   *
   * Increases take effect immediately; decreases require DELAY_DECREASE_SAMPLES
   * consecutive samples (~8 seconds at 500ms cadence) of sustained low P90 to
   * avoid bouncing on transient dips.
   */
  recordRttSample(rttMs) {
    this._lastRtt = rttMs;
    this._rttSamples.push(rttMs);
    if (this._rttSamples.length > RTT_HISTORY_SIZE) this._rttSamples.shift();

    const useHistory = this._rttSamples.length >= MIN_HISTORY_FOR_P90;
    const basis = useHistory ? this._computeP90() : rttMs;
    const clamped = computeLockstepInputDelay(basis, this._frameDurationMs);

    if (clamped > this.inputDelay) {
      this.inputDelay = clamped;
      this._decreaseStreak = 0;
    } else if (clamped < this.inputDelay && useHistory) {
      this._decreaseStreak++;
      if (this._decreaseStreak >= DELAY_DECREASE_SAMPLES) {
        this.inputDelay = clamped;
        this._decreaseStreak = 0;
      }
    } else {
      this._decreaseStreak = 0;
    }
  }

  /**
   * Thin alias for backwards compatibility. Prefer recordRttSample for new code.
   */
  adjustDelay(rttMs) {
    this.recordRttSample(rttMs);
  }

  reset() {
    this.currentFrame = 0;
    this._pendingLocalInput = 0;
    this.localInputs = {};
    this.remoteInputs = {};
    for (let f = 0; f < this.inputDelay; f++) {
      this.localInputs[f] = 0;
      this.remoteInputs[f] = 0;
    }
    this._nextLocalInputFrame = this.inputDelay;
    this.stallCount = 0;
    this.totalFrames = 0;
    this._stallStart = 0;
    this._totalStallMs = 0;
    this._lastSyncFrame = 0;
    this._localSyncHashes = {};
    this._remoteSyncHashes = {};
    this.desyncDetected = false;
    this.desyncFrame = -1;
  }

  _computeP90() {
    const n = this._rttSamples.length;
    if (n === 0) return 0;
    const sorted = [...this._rttSamples].sort((a, b) => a - b);
    const idx = Math.min(n - 1, Math.ceil(n * RTT_PERCENTILE) - 1);
    return sorted[idx];
  }

  _sendLocalInputsThrough(targetFrame) {
    while (this._nextLocalInputFrame <= targetFrame) {
      const frame = this._nextLocalInputFrame;
      this.localInputs[frame] = this._pendingLocalInput;
      if (this.onSendInput) {
        this.onSendInput(frame, this._pendingLocalInput);
      }
      this._nextLocalInputFrame++;
    }
  }

  getStats() {
    const healthPct = this.totalFrames > 0
      ? Math.round(((this.totalFrames - this.stallCount) / this.totalFrames) * 100)
      : 100;
    const totalStallMs = this._stallStart
      ? this._totalStallMs + (performance.now() - this._stallStart)
      : this._totalStallMs;
    const meanStallMs = this.stallCount > 0 ? totalStallMs / this.stallCount : 0;
    const rttP90 = this._rttSamples.length >= MIN_HISTORY_FOR_P90
      ? this._computeP90()
      : null;
    return {
      frame: this.currentFrame,
      delay: this.inputDelay,
      stalls: this.stallCount,
      health: healthPct,
      rtt: this._lastRtt,
      rttP90,
      totalStallMs,
      meanStallMs,
      desync: this.desyncDetected,
      desyncFrame: this.desyncFrame,
    };
  }

  _checkDesync(frame) {
    const local = this._localSyncHashes[frame];
    const remote = this._remoteSyncHashes[frame];
    if (local === undefined || remote === undefined) return;

    if (local !== remote && !this.desyncDetected) {
      this.desyncDetected = true;
      this.desyncFrame = frame;
      if (this.onDesync) {
        this.onDesync(frame, local, remote);
      }
    }

    delete this._localSyncHashes[frame];
    delete this._remoteSyncHashes[frame];
  }

  _gc() {
    const cutoff = this.currentFrame - 30;
    for (const frame in this.localInputs) {
      if (+frame < cutoff) delete this.localInputs[frame];
    }
    for (const frame in this.remoteInputs) {
      if (+frame < cutoff) delete this.remoteInputs[frame];
    }
    const syncCutoff = this.currentFrame - this._syncInterval * 2;
    for (const frame in this._localSyncHashes) {
      if (+frame < syncCutoff) delete this._localSyncHashes[frame];
    }
    for (const frame in this._remoteSyncHashes) {
      if (+frame < syncCutoff) delete this._remoteSyncHashes[frame];
    }
  }

  destroy() {
    this.onSendInput = null;
    this.onSendSync = null;
    this.onDesync = null;
    this.onApplyInputs = null;
  }
}
